<template>
  <div class="order-detail">
    <div class="back-btn" @click="goBack">← 返回列表</div>
    
    <div class="detail-header">
      <div class="order-id">ID: {{ orderDetail.id }}</div>
      <div class="header-left">
        <div class="order-no">订单号: {{ orderDetail.order_no }}</div>
      </div>
      <span :class="'status status-' + orderDetail.order_status">当前状态：{{ getStatusName(orderDetail.order_status) }}</span>
    </div>

    <div class="detail-section">
      <h3>服务信息</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>服务类型</label>
          <span>{{ orderDetail.service_type_text || getServiceName(orderDetail.service_type_code) }}</span>
        </div>
        <div class="info-item">
          <label>下单时备注</label>
          <span>{{ orderDetail.remark || '-' }}</span>
        </div>
        <div class="info-item">
          <label>订单金额</label>
          <span class="amount">{{ centToYuan(orderDetail.pay_amount) }}</span>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h3>费用明细</h3>
      <div class="fee-grid">
        <div class="fee-item">
          <label>起步价</label>
          <span :class="startFee > 0 ? 'amount' : ''">{{ formatFee(startFee) }}</span>
        </div>
        <div class="fee-item">
          <label>搬运费总价合计</label>
          <span :class="carryFees > 0 ? 'amount' : ''">{{ formatFee(carryFees) }}</span>
        </div>
        <div class="fee-item">
          <label>楼层费总价合计</label>
          <span :class="floorFees > 0 ? 'amount' : ''">{{ formatFee(floorFees) }}</span>
        </div>
        <div class="fee-item">
          <label>特殊时段费</label>
          <span :class="specialTimeFees > 0 ? 'amount' : ''">{{ formatFee(specialTimeFees) }}</span>
        </div>
        <div class="fee-item">
          <label>特殊天气费</label>
          <span :class="specialWeatherFees > 0 ? 'amount' : ''">{{ formatFee(specialWeatherFees) }}</span>
        </div>
        <div class="fee-item">
          <label>加急加价</label>
          <span :class="boostFees > 0 ? 'amount' : ''">{{ formatFee(boostFees) }}</span>
        </div>
        <div class="fee-item">
          <label>优惠减免</label>
          <span>-{{ formatFee(discountFee) }}</span>
        </div>
        <div class="fee-item">
          <label>实付金额</label>
          <span class="amount">{{ formatFee(orderDetail.pay_amount) }}</span>
        </div>
        <div class="fee-item">
          <label>物品总价合计</label>
          <span>{{ formatFee(sale_amount_total) }}</span>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h3>物品清单 与 搬运费清单</h3>
      <table v-if="orderItems.length" class="sku-table">
        <thead>
          <tr>
            <th>物品</th>
            <th>规格</th>
            <th>数量</th>
            <th>物品单价</th>
            <th>物品总价</th>
            <th>搬运费单价</th>
            <th>搬运费总价</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in orderItems" :key="item.id">
            <td>
              <div class="sku-name">{{ item.product_name || '-' }}</div>
              <img
                v-if="item.product && item.product.image"
                :src="item.product.image"
                class="sku-thumb"
                alt=""
                @error="e => e.target.style.display = 'none'"
              >
            </td>
            <td>{{ item.spec_snapshot || '-' }}</td>
            <td>× {{ item.num || 1 }}</td>
            <td>{{ formatFee(item.sale_price) }}</td>
            <td>{{ formatFee(item.sale_amount || 0) }}</td>
            <td>{{ formatFee(item.carry_fee) }}</td>
            <td>{{ formatFee(item.carry_fees) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" class="carry-total-label">合计</td>
            <td class="amount">{{ formatFee(sale_amount_total) }}</td>
            <td></td>
            <td class="amount">{{ formatFee(carry_fees_total) }}</td>
          </tr>
        </tfoot>
      </table>
      <div v-else class="sku-empty">暂无物品信息</div>
    </div>

    <div class="detail-section">
      <h3>取货地址</h3>
      <div class="address-card">
        <div class="address-icon">📍</div>
        <div class="address-content">
          <div class="address-detail">
            {{ orderDetail.community_name }}
            {{ orderDetail.from_address_detail }} -
            {{ orderDetail.shop_name }}
          </div>
          <div class="address-title">
            {{ orderDetail.user_name }}
            {{ orderDetail.phone }}
          </div>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h3>收货地址</h3>
      <div class="address-card">
        <div class="address-icon">🏠</div>
        <div class="address-content">
          <div class="address-detail">
            {{ orderDetail.community_name }}
            {{ orderDetail.to_address_detail }}
          </div>
          <div class="address-title">
            {{ orderDetail.receiver_name }}
            {{ orderDetail.receiver_phone }}
          </div>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h3>下单用户信息</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>用户姓名</label>
          <span>{{ orderDetail.user_name }}</span>
        </div>
        <div class="info-item">
          <label>联系电话</label>
          <span>{{ orderDetail.phone }}</span>
        </div>
        <div class="info-item">
          <label>所属小区</label>
          <span>{{ orderDetail.community_name || '-' }}</span>
        </div>
      </div>
    </div>

    <div class="detail-section" v-if="orderDetail.runner_name">
      <h3>跑手信息</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>跑手姓名</label>
          <span>{{ orderDetail.runner_name }}</span>
        </div>
        <div class="info-item">
          <label>联系电话</label>
          <span>{{ orderDetail.runner_phone || '-' }}</span>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h3>订单日志</h3>
      <div class="timeline">
        <div v-for="(log, index) in orderTimeline" :key="index" class="timeline-item">
          <div class="timeline-dot"></div>
          <div class="timeline-content">
            <div class="timeline-time">{{ log.time }}</div>
            <div class="timeline-text">{{ log.text }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="detail-section" v-if="commissionAllocations.length">
      <h3>服务费分配详情</h3>
      <div class="allocation-summary">
        <span>共 {{ commissionAllocations.length }} 条分配记录</span>
        <span>分配合计：<b>{{ formatFee(allocationTotal) }}</b> 元</span>
      </div>
      <table class="allocation-table">
        <thead>
          <tr>
            <th>角色</th>
            <th>服务费单项</th>
            <th>分配金额</th>
            <th>计算方式</th>
            <th>比例/固定</th>
            <th>结算状态</th>
            <th>退款状态</th>
            <th>备注</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="alloc in commissionAllocations" :key="alloc.id">
            <td>{{ (alloc.role && alloc.role.name) || '-' }}</td>
            <td>{{ commissionItemTypeMap[alloc.commission_item_type] || '未知' }}</td>
            <td class="amount">{{ formatFee(Number(alloc.total_amount) || 0) }} 元</td>
            <td>{{ calcTypeMap[alloc.calc_calculation_type] || '-' }}</td>
            <td>
              <template v-if="alloc.calc_calculation_type === 1">
                {{ formatFee(Number(alloc.calc_fixed_amount) || 0) }} 元
              </template>
              <template v-else-if="alloc.calc_allocation_rate != null">
                {{ alloc.calc_allocation_rate / 100 }}%
              </template>
              <template v-else>-</template>
            </td>
            <td>
              <span class="status-tag" :class="'settle-' + alloc.settle_status">
                {{ settleStatusMap[alloc.settle_status] || '未知' }}
              </span>
            </td>
            <td>
              <span class="status-tag" :class="'refund-' + alloc.refund_status">
                {{ refundStatusMap[alloc.refund_status] || '未知' }}
              </span>
              <span v-if="alloc.refund_amount" class="refund-amount">
                (退 {{ formatFee(Number(alloc.refund_amount) || 0) }} 元)
              </span>
            </td>
            <td>{{ alloc.remark || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getOrderDetail, getOrderItems } from '@/api/orders'
import { getOrderCommissionAllocations } from '@/api/commissionAllocations'
import { centToYuan } from '@/utils/money'
import { formatDateTime } from '@/utils/date'

export default {
  name: 'OrderDetail',
  data() {
    return {
      orderDetail: {},
      orderItems: [],
      commissionAllocations: []
    }
  },
  mounted() {
    this.fetchOrderDetail()
  },
  computed: {
    // 物品总价 = 各明细 sale_amount 之和（后端已冻结，无需前端计算）
    sale_amount_total() {
      return this.orderItems.reduce((sum, item) => sum + (item.sale_amount || 0), 0)
    },
    // 搬运费合计 = 各明细 carry_fees（搬运费总价）之和
    carry_fees_total() {
      return this.orderItems.reduce((sum, item) => sum + (item.carry_fees || 0), 0)
    },
    startFee() {
      return this.orderDetail.start_fee || 0
    },
    carryFees() {
      return this.orderDetail.carry_fees || 0
    },
    floorFees() {
      return this.orderDetail.floor_fees || 0
    },
    specialTimeFees() {
      return this.orderDetail.special_time_fees || 0
    },
    specialWeatherFees() {
      return this.orderDetail.special_weather_fees || 0
    },
    boostFees() {
      return this.orderDetail.boost_fees || 0
    },
    discountFee() {
      const o = this.orderDetail
      return (o.point_amount || 0)
    },
    // 服务费单项类型文本（对齐后端：分配记录 commission_item_type 列存的是 OrderCommissionItem 枚举，
    // 现已与规则表枚举统一：0=通用，1~6=起步/距离/搬运/楼层/时段/天气，7=加急加价服务费）
    commissionItemTypeMap() {
      return {
        0: '通用(全部服务费)',
        1: '起步服务费',
        2: '距离服务费',
        3: '搬运服务费',
        4: '楼层服务费',
        5: '特殊时段服务费',
        6: '特殊天气服务费',
        7: '加急加价服务费'
      }
    },
    // 计算方式文本（对齐后端 OrderCommissionAllocation 校验）
    calcTypeMap() {
      return {
        1: '固定金额',
        2: '基于总服务费%',
        3: '基于跑手服务费%',
        4: '基于服务费单个项目%'
      }
    },
    // 结算状态文本（对齐后端 OrderCommissionAllocation 模型）
    settleStatusMap() {
      return {
        0: '未结算',
        1: '已结算',
        2: '结算中'
      }
    },
    // 退款状态文本
    refundStatusMap() {
      return {
        0: '无退款',
        1: '申请退款',
        2: '退款中',
        3: '已退款'
      }
    },
    // 服务费分配合计（元）
    allocationTotal() {
      return this.commissionAllocations.reduce(
        (sum, a) => sum + (Number(a.total_amount) || 0),
        0
      )
    },
    orderTimeline() {
      const timeline = []
      const o = this.orderDetail
      
      if (o.created_at) {
        timeline.push({ time: formatDateTime(o.created_at), text: '用户提交订单' })
      }
      if (o.runner_accept_time) {
        timeline.push({ time: formatDateTime(o.runner_accept_time), text: `${o.runner_name || '跑手'}已接单` })
      }
      if (o.runner_pickup_time) {
        timeline.push({ time: formatDateTime(o.runner_pickup_time), text: '跑手已取货' })
      }
      if (o.runner_arrive_time) {
        timeline.push({ time: formatDateTime(o.runner_arrive_time), text: '订单已完成' })
      }
      if (o.cancel_time) {
        timeline.push({ time: formatDateTime(o.cancel_time), text: '订单已取消' })
      }
      
      return timeline.sort((a, b) => new Date(a.time) - new Date(b.time))
    }
  },
  methods: {
    centToYuan,
    getServiceName(type) {
      const map = {
        DELIVERY: '代送物品',
        PURCHASE: '代买物品',
        PICKUP: '代取快递',
        OTHER: '其他服务',
        ALL: '全部',
        1: '代送物品',
        2: '代买物品',
        3: '代取快递',
        4: '其他服务'
      }
      return map[type] || '未知'
    },
    getStatusName(status) {
      const map = { 
        0: '待付款', 
        1: '待接单', 
        2: '已接单', 
        3: '进行中', 
        4: '已送达', 
        5: '已完成',
        6: '用户已取消',
        7: '商家已取消',
        8: '跑手已取消',
        9: '跑手超时未接单',
        10: '售后中'
      }
      return map[status] || '未知'
    },
    async fetchOrderDetail() {
      const id = this.$route.params.id
      try {
        const [detailRes, itemsRes, allocationRes] = await Promise.all([
          getOrderDetail(id),
          getOrderItems(id),
          getOrderCommissionAllocations(id)
        ])
        if (detailRes.code === 0) {
          this.orderDetail = detailRes.data || {}
        }
        if (itemsRes.data) {
          this.orderItems = Array.isArray(itemsRes.data) ? itemsRes.data : []
        }
        if (allocationRes.data) {
          const d = allocationRes.data
          this.commissionAllocations = Array.isArray(d) ? d : (d.data || [])
        }
      } catch (error) {
      }
    },
    formatFee(value) {
      return centToYuan(value)
    },
    goBack() {
      this.$router.push('/orders')
    }
  }
}
</script>

<style scoped>
.order-detail {
  padding: 20px;
}

.back-btn {
  color: #3498db;
  cursor: pointer;
  margin-bottom: 20px;
  font-size: 14px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.order-no {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.order-id {
  font-size: 16px;
  color: #999;
}

.status {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
}

.status-0 { background: #fff3e0; color: #f39c12; }
.status-1 { background: #fff3e0; color: #f39c12; }
.status-2 { background: #e3f2fd; color: #3498db; }
.status-3 { background: #e8f5e9; color: #2ecc71; }
.status-4 { background: #e8f5e9; color: #2ecc71; }
.status-5 { background: #f5f5f5; color: #666; }
.status-6 { background: #ffebee; color: #e74c3c; }
.status-7 { background: #ffebee; color: #e74c3c; }
.status-8 { background: #ffebee; color: #e74c3c; }
.status-9 { background: #ffebee; color: #e74c3c; }
.status-10 { background: #fff3e0; color: #f39c12; }

.detail-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-section h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.info-item span.amount {
  color: #e74c3c;
  font-weight: 600;
}

.fee-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.fee-item {
  display: flex;
  flex-direction: column;
}

.fee-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.fee-item span {
  font-size: 14px;
  color: #333;
}

.fee-item span.amount {
  color: #e74c3c;
  font-weight: 600;
}

.address-card {
  display: flex;
  gap: 15px;
}

.address-icon {
  font-size: 24px;
}

.address-content {
  flex: 1;
}

.address-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 5px;
}

.address-detail {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.timeline {
  padding-left: 10px;
}

.timeline-item {
  display: flex;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px dashed #eee;
}

.timeline-item:last-child {
  border-bottom: none;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3498db;
  margin-top: 5px;
}

.timeline-content {
  flex: 1;
}

.timeline-time {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.timeline-text {
  font-size: 14px;
  color: #333;
}

.sku-table {
  width: 100%;
  border-collapse: collapse;
}

.sku-table th,
.sku-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
  font-size: 14px;
  color: #333;
  vertical-align: top;
}

.sku-table th {
  color: #999;
  font-weight: 500;
  background: #fafafa;
}

.sku-table td.amount {
  color: #e74c3c;
  font-weight: 600;
}

.sku-name {
  margin-bottom: 6px;
}

.sku-thumb {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  display: block;
}

.sku-empty {
  padding: 10px 0;
  font-size: 14px;
  color: #999;
}

.sku-table tfoot td {
  border-top: 2px solid #eee;
  padding-top: 12px;
  font-weight: 600;
}

.carry-total-label {
  text-align: right;
  color: #666;
}

.allocation-summary {
  display: flex;
  gap: 24px;
  margin-bottom: 15px;
  font-size: 14px;
  color: #666;
}

.allocation-summary b {
  color: #e74c3c;
}

.allocation-table {
  width: 100%;
  border-collapse: collapse;
}

.allocation-table th,
.allocation-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #eee;
  text-align: left;
  font-size: 14px;
  color: #333;
  vertical-align: top;
}

.allocation-table th {
  color: #999;
  font-weight: 500;
  background: #fafafa;
}

.allocation-table td.amount {
  color: #e74c3c;
  font-weight: 600;
}

.status-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
}

.settle-0 { background: #fff3e0; color: #f39c12; }
.settle-1 { background: #e8f5e9; color: #2ecc71; }
.settle-2 { background: #e3f2fd; color: #3498db; }

.refund-0 { background: #f5f5f5; color: #999; }
.refund-1 { background: #fff3e0; color: #f39c12; }
.refund-2 { background: #e3f2fd; color: #3498db; }
.refund-3 { background: #ffebee; color: #e74c3c; }

.refund-amount {
  margin-left: 6px;
  font-size: 12px;
  color: #e74c3c;
}
</style>
